// src/rms/i18n/index.js
// ============================================================================
// Translation runtime
// ============================================================================
// i18next with the ICU message format, wired to the active locale in
// ./locale.js. Two decisions worth stating, because both are easy to get wrong
// and expensive to change later.
//
// ICU, not i18next's own interpolation
// ------------------------------------
// Arabic has six plural forms and Russian has four. i18next's built-in
// `_plural` suffixes cover the two-form case that English and German need and
// then quietly mishandle the rest -- "٣ حجوزات" would come out with the wrong
// noun form for zero, two, and the 3-10 and 11-99 bands. ICU MessageFormat is
// the standard that encodes those rules properly, and CLDR already knows them
// for all thirteen locales. The cost is that catalogue entries look like
// `{count, plural, one {...} other {...}}` rather than plain strings.
//
// Catalogues are loaded on demand
// -------------------------------
// Thirteen languages bundled into the entry point would be shipped in full to
// every visitor so that each one could read exactly one of them. They are
// discovered with import.meta.glob, so a locale is present exactly when its
// JSON file is -- adding a language means adding a file and a row in
// SUPPORTED_LOCALES, with no import list to keep in sync and no build error
// for a language that has not been translated yet.
// ============================================================================

import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import ICU from "i18next-icu";
import { DEFAULT_LOCALE, getLocale, subscribeToLocale } from "./locale";

// Vite resolves this at build time into a map of path -> dynamic import.
const catalogues = import.meta.glob("./catalogues/*.json");

const pathFor = (code) => `./catalogues/${code}.json`;

/** Whether a translation file actually exists for this locale. */
export const hasCatalogue = (code) => Boolean(catalogues[pathFor(code)]);

const loaded = new Set();

/**
 * Loads a locale's catalogue once and registers it.
 *
 * A missing catalogue is not an error: a locale can be listed as supported
 * before anyone has translated it, and i18next will fall back to English key
 * by key. That is a far better failure than a blank screen, and it means
 * partial translations ship usefully instead of blocking on completeness.
 */
export async function loadCatalogue(code) {
  if (loaded.has(code)) return true;
  const loader = catalogues[pathFor(code)];
  if (!loader) return false;

  const module = await loader();
  i18n.addResourceBundle(code, "translation", module.default || module, true, true);
  loaded.add(code);
  return true;
}

let initialised = null;

export function initI18n() {
  if (initialised) return initialised;

  initialised = i18n
    .use(ICU)
    .use(initReactI18next)
    .init({
      lng: getLocale(),
      fallbackLng: DEFAULT_LOCALE,
      // Flat keys namespaced with dots ("nav.dashboard") rather than nested
      // objects, so a key reads the same in the catalogue as in the call site.
      keySeparator: false,
      nsSeparator: false,
      defaultNS: "translation",
      resources: {},
      interpolation: {
        // React escapes on render; doing it again turns an apostrophe into
        // &#39; on screen.
        escapeValue: false,
      },
      returnEmptyString: false,
      // No parseMissingKeyHandler here, deliberately. Setting one overrides the
      // second argument to t(), so every t("nav.dashboard", "Dashboard") in the
      // codebase rendered the raw key instead of its English fallback -- which
      // made the whole extract-with-a-fallback pattern silently useless. Left
      // alone, i18next uses the fallback when there is one and returns the key
      // when there is not, which is what was wanted in the first place.
    })
    .then(async () => {
      await loadCatalogue(DEFAULT_LOCALE);
      const current = getLocale();
      if (current !== DEFAULT_LOCALE) {
        await loadCatalogue(current);
        await i18n.changeLanguage(current);
      }
      return i18n;
    });

  // Keep i18next in step with the app's locale. The catalogue is loaded BEFORE
  // the language changes, so the UI never flashes a screen of raw keys while a
  // network request is in flight.
  subscribeToLocale(async (code) => {
    await loadCatalogue(code);
    if (i18n.language !== code) await i18n.changeLanguage(code);
  });

  return initialised;
}

export default i18n;
